import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FulfillmentOrderConfig, FulfillmentOrderConfigApiService } from '@vendasta/marketplace-apps';
import { FulfillmentOrder, FulfillmentOrderApiService, FulfillmentOrderStatus } from '@vendasta/order-fulfillment';
import { User as LibUser } from '@vendasta/users';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs';
import { SendEmailDialogComponent, SendEmailDialogResp } from '../send-email-dialog/send-email-dialog.component';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import { User } from '../user';
import {
  clearUnsavedChangesFromSessionStorage,
  doesWorkOrderFormHaveUnsavedChanges,
  getTemporaryStorageKey,
} from '../utils';
import { WorkOrderChecklistComponent } from '../work-order-checklist/work-order-checklist.component';
import { WorkOrderContextComponent } from '../work-order-context/work-order-context.component';
import { WorkOrderFormComponent } from '../work-order-form/work-order-form.component';
import { WorkOrderFormService } from '../work-order-form/work-order-form.service';
import { WorkOrderPersona } from '../work-order-landing-page/interfaces';
import { WorkOrderModule } from '../work-order.module';
import {
  WorkOrderUnsavedChangesDialogComponent,
  WorkOrderUnsavedChangesDialogResult,
} from './work-order-unsaved-changes-dialog/work-order-unsaved-changes-dialog.component';
import { WorkOrderUnsavedChangesComponent } from './work-orders-unsaved-changes.guard';

@Component({
  selector: 'work-order-details',
  imports: [
    CommonModule,
    WorkOrderModule,
    WorkOrderFormComponent,
    GalaxyPageModule,
    TranslateModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    GalaxyPopoverModule,
    WorkOrderChecklistComponent,
    WorkOrderContextComponent,
    StatusBadgeComponent,
  ],
  templateUrl: './work-order-details.component.html',
  styleUrls: ['./work-order-details.component.scss'],
})
export class WorkOrderDetailsComponent
  extends WorkOrderUnsavedChangesComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  @Input() partnerId: string;
  @Input() marketId: string;
  @Input() businessId: string;
  @Input() salesOrderId: string;
  @Input() persona: WorkOrderPersona = WorkOrderPersona.CP;
  @Input() set users(users: User[]) {
    this.allUsers = users?.map((user) => this.convertToLibUser(user)) || [];
  }
  @Input() showOfficeUseQuestions = true;
  @Input() previousPageUrl: string;
  @Input() hidePageWrapper = false;
  @Input() allowUserManagement = false;

  private workOrders$$ = new BehaviorSubject<FulfillmentOrder[]>([]);
  readonly workOrders$ = this.workOrders$$.asObservable();
  readonly detailsNeeded$ = this.workOrders$$.pipe(
    map((workOrders) => {
      if (!workOrders?.length) {
        return false;
      }
      return workOrders.some((wo) => wo.status === FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED);
    }),
  );
  private refreshWorkOrders$$ = new BehaviorSubject<void>(null);
  allUsers: LibUser[];
  WorkOrderPersona = WorkOrderPersona;

  subscriptions: Subscription[] = [];

  @ViewChildren(StatusBadgeComponent) statusBadges!: QueryList<StatusBadgeComponent>;
  @ViewChildren(WorkOrderFormComponent) workOrderForms: QueryList<WorkOrderFormComponent>;
  combinedFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  popoverPosition = [PopoverPositions.BottomRight];
  private woFormDoneLoadingMap$$: BehaviorSubject<Map<number, boolean>> = new BehaviorSubject(
    new Map<number, boolean>(),
  );
  woFormDoneLoading$: Observable<boolean>;

  constructor(
    private route: ActivatedRoute,
    private workOrderFormService: WorkOrderFormService,
    public dialog: MatDialog,
    private snackbarService: SnackbarService,
    private fulfillmentOrderService: FulfillmentOrderApiService,
    private fulfillmentOrderConfigApiService: FulfillmentOrderConfigApiService,
    private destroyRef: DestroyRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.refreshWorkOrders$$
      .pipe(
        switchMap(() =>
          this.workOrderFormService.getOrdersToRenderFormsFor(
            this.salesOrderId,
            this.businessId,
            this.persona === WorkOrderPersona.SMB,
          ),
        ),
        switchMap((workOrders) => {
          return this.filterOrdersWithForms(workOrders);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((workOrders) => this.workOrders$$.next(workOrders));

    this.workOrders$.pipe(take(1)).subscribe((workOrders) => {
      workOrders.forEach((_, i) => {
        const loadingMap: Map<number, boolean> = this.woFormDoneLoadingMap$$.getValue();
        loadingMap.set(i, false);
        this.woFormDoneLoadingMap$$.next(loadingMap);
      });
    });

    this.woFormDoneLoading$ = this.woFormDoneLoadingMap$$.pipe(
      map((woLoadingMap) => {
        const loadingValues: boolean[] = Array.from(woLoadingMap.values());
        return loadingValues?.length > 0 && loadingValues.every((value) => value);
      }),
    );
  }

  ngAfterViewInit(): void {
    const fragment$: Observable<string> = this.route.fragment;
    const statusBadges$: Observable<unknown> = this.statusBadges.changes;
    this.subscriptions.push(
      combineLatest([fragment$, statusBadges$])
        .pipe()
        .subscribe(([fragment, statusBadges]: [string, QueryList<StatusBadgeComponent>]) => {
          const fragmentBadge = statusBadges.find((sb) => sb?.['fulfillmentOrderId'] === fragment);
          if (fragmentBadge) {
            // page is done loading and found the correct fragment
            document.getElementById(fragment)?.scrollIntoView();
          }
        }),
    );
    this.workOrderForms.changes.pipe(map((a) => a.toArray())).subscribe((workOrderForms) => {
      workOrderForms.forEach((woFormComp) => {
        const key: string = getTemporaryStorageKey(woFormComp.accountGroupId, woFormComp.appId);
        if (!this.combinedFormGroup.contains(key)) {
          this.combinedFormGroup.setControl(key, woFormComp.form);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  refreshWorkOrders(): void {
    this.refreshWorkOrders$$.next(null);
  }

  statusChanged(newStatus: FulfillmentOrderStatus, wo: FulfillmentOrder): void {
    const actualWorkOrders: FulfillmentOrder[] = this.workOrders$$.getValue();
    const index: number = actualWorkOrders.findIndex((w) => w.fulfillmentOrderId === wo.fulfillmentOrderId);
    if (index > -1) {
      actualWorkOrders[index].status = newStatus;
      this.workOrders$$.next(actualWorkOrders);
    }
  }

  openSendEmailDialog(): void {
    const afterClosed$: Observable<SendEmailDialogResp> = this.dialog
      .open(SendEmailDialogComponent, {
        width: '600px',
        data: {
          allUsers: this.allUsers,
          partnerId: this.partnerId,
          businessId: this.businessId,
          allowUserManagement: this.allowUserManagement,
        },
      })
      .afterClosed();

    combineLatest([this.workOrders$$, afterClosed$])
      .pipe(
        take(1),
        switchMap(([workOrders, resp]) => {
          if (!resp?.userIds || resp?.userIds?.length === 0) {
            return of(undefined);
          }

          const itemNames = workOrders.map((wo) => wo.appInfo.appName);
          return from(resp.userIds).pipe(
            mergeMap((userId) =>
              this.fulfillmentOrderService.sendFulfillmentOrder({
                businessId: this.businessId,
                salesOrderId: this.salesOrderId,
                userId: userId,
                itemNames: itemNames,
              }),
            ),
            toArray(),
          );
        }),
      )
      .subscribe({
        next: (resp) => {
          if (resp) {
            this.snackbarService.openSuccessSnack('Successfully sent email');
          }
        },
        error: () => this.snackbarService.openErrorSnack('Failed to send email'),
      });
  }

  filterOrdersWithForms(orders: FulfillmentOrder[]): Observable<FulfillmentOrder[]> {
    if (orders.length === 0) {
      return of([]);
    }

    const apiCalls: Observable<FulfillmentOrderConfig>[] = orders.map((order) => {
      return this.fulfillmentOrderConfigApiService
        .getFulfillmentOrderConfig({
          appId: order.appId,
        })
        .pipe(map((response) => response.config));
    });

    return forkJoin(apiCalls).pipe(
      map((configs) =>
        orders.filter((order) => configs.find((config) => config.appId === order.appId)?.usesFulfillmentForm),
      ),
    );
  }

  canDeactivate(): boolean {
    return !doesWorkOrderFormHaveUnsavedChanges(this.combinedFormGroup);
  }

  pendingChangesDialog(): boolean | Observable<boolean> {
    this.dialog.closeAll();
    return this.dialog
      .open(WorkOrderUnsavedChangesDialogComponent)
      .afterClosed()
      .pipe(
        map((result: WorkOrderUnsavedChangesDialogResult) => {
          return result === WorkOrderUnsavedChangesDialogResult.CONTINUE_EDITING ? false : !!result;
        }),
        tap((result: boolean) => {
          if (result) {
            clearUnsavedChangesFromSessionStorage(this.combinedFormGroup);
          }
        }),
      );
  }

  handleDoneLoading(index: number): void {
    const loadingMap: Map<number, boolean> = this.woFormDoneLoadingMap$$.getValue();
    loadingMap.set(index, true);
    this.woFormDoneLoadingMap$$.next(loadingMap);
  }

  public convertToLibUser(user: User): LibUser {
    return {
      userId: user.userId,
      unifiedId: user.unifiedUserId,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
    } as LibUser;
  }
}
